import { Link, useLocation, useNavigate } from 'react-router-dom';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { Users, ShieldCheck, BarChart3, LogOut, Menu, X, Award, Send, Search, CalendarCheck } from 'lucide-react';
import { AuthContext } from "../../context/Auth";
import { Avatar } from '../ui/Avatar';

export const NAV_GROUPS = [
  {
    label: 'Visão geral',
    links: [
      { to: '/dashboard', label: 'Usuários', icon: Users },
      { to: '/reports', label: 'Relatórios', icon: BarChart3 },
    ],
  },
  {
    label: 'Crescimento',
    links: [
      { to: '/ambassadors', label: 'Embaixadoras', icon: Award },
      { to: '/broadcasts', label: 'Disparos', icon: Send },
    ],
  },
  {
    label: 'Sistema',
    links: [
      { to: '/admins', label: 'Administradores', icon: ShieldCheck },
    ],
  },
];

const ALL_LINKS = NAV_GROUPS.flatMap((g) => g.links);

const Brand = () => (
  <div className="flex items-center gap-3">
    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#8B6CFF] to-accent text-white shadow-sm shadow-accent/30">
      <CalendarCheck className="h-5 w-5" />
    </div>
    <div className="min-w-0">
      <p className="truncate text-[15px] font-semibold leading-tight text-gray-900">Agendix</p>
      <p className="truncate text-xs text-gray-500">Painel administrativo</p>
    </div>
  </div>
);

// Conteúdo da sidebar, igual no desktop e no drawer do celular
const SidebarContent = ({ onNavigate }) => {
  const { signOut, user } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const searchRef = useRef(null);

  // ⌘K / Ctrl+K foca a busca de telas
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const q = query.trim().toLowerCase();
  const groups = NAV_GROUPS
    .map((g) => ({ ...g, links: g.links.filter((l) => !q || l.label.toLowerCase().includes(q)) }))
    .filter((g) => g.links.length);

  const handleSearchKey = (e) => {
    if (e.key === 'Enter') {
      const first = ALL_LINKS.find((l) => l.label.toLowerCase().includes(q));
      if (first) {
        setQuery('');
        searchRef.current?.blur();
        navigate(first.to);
        onNavigate?.();
      }
    } else if (e.key === 'Escape') {
      setQuery('');
      searchRef.current?.blur();
    }
  };

  const isMac = typeof navigator !== 'undefined' && /Mac/i.test(navigator.platform);
  const displayName = user?.username || localStorage.getItem('@Auth:name') || 'Admin';

  return (
    <div className="flex h-full flex-col">
      <div className="px-4 pb-4 pt-5">
        <Brand />
        <div className="relative mt-5">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            ref={searchRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleSearchKey}
            placeholder="Buscar tela..."
            className="w-full rounded-xl border border-gray-200 bg-white py-2 pl-9 pr-12 text-sm text-gray-900 placeholder-gray-400 shadow-card focus:border-accent/40 focus:outline-none focus:ring-2 focus:ring-accent/15"
          />
          <kbd className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 rounded-md border border-gray-200 bg-gray-50 px-1.5 py-0.5 font-sans text-[10px] font-medium text-gray-400">
            {isMac ? '⌘K' : 'Ctrl K'}
          </kbd>
        </div>
      </div>

      <nav className="flex-1 space-y-5 overflow-y-auto px-3 pb-4">
        {groups.map((group) => (
          <div key={group.label}>
            <p className="px-3 pb-1.5 text-[11px] font-medium uppercase tracking-wider text-gray-400">{group.label}</p>
            <div className="space-y-0.5">
              {group.links.map(({ to, label, icon: Icon }) => {
                const active = location.pathname === to;
                return (
                  <Link
                    key={to}
                    to={to}
                    onClick={() => { setQuery(''); onNavigate?.(); }}
                    className={`group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition ${
                      active
                        ? 'bg-white font-semibold text-gray-900 shadow-pill'
                        : 'text-gray-600 hover:bg-white/70 hover:text-gray-900'
                    }`}
                  >
                    <Icon className={`h-[18px] w-[18px] ${active ? 'text-accent' : 'text-gray-400 group-hover:text-gray-600'}`} strokeWidth={active ? 2.25 : 1.75} />
                    {label}
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
        {!groups.length && <p className="px-3 text-sm text-gray-400">Nenhuma tela encontrada</p>}
      </nav>

      <div className="m-3 flex items-center gap-3 rounded-xl bg-white p-2.5 shadow-pill">
        <Avatar name={displayName} size="sm" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-gray-900">{displayName}</p>
          <p className="truncate text-xs text-gray-500">{user?.email}</p>
        </div>
        <button
          onClick={signOut}
          title="Sair"
          className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg text-gray-400 transition hover:bg-rose-50 hover:text-rose-600"
        >
          <LogOut className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export const Sidebar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <>
      {/* Topbar mobile */}
      <header className="fixed left-0 top-0 z-30 flex w-full items-center justify-between border-b border-gray-200/70 bg-white/90 px-4 py-3 backdrop-blur lg:hidden">
        <Brand />
        <button onClick={() => setMobileOpen(true)} className="rounded-lg p-2 text-gray-600 hover:bg-gray-100" aria-label="Abrir menu">
          <Menu className="h-5 w-5" />
        </button>
      </header>
      <div className="h-16 lg:hidden" />

      {/* Drawer mobile */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 flex lg:hidden">
          <div className="absolute inset-0 animate-fade-in bg-gray-900/30" onClick={() => setMobileOpen(false)} />
          <div className="relative w-72 bg-subtle shadow-xl">
            <button
              onClick={() => setMobileOpen(false)}
              className="absolute right-3 top-5 z-10 rounded-lg p-1.5 text-gray-400 hover:bg-white hover:text-gray-600"
              aria-label="Fechar menu"
            >
              <X className="h-5 w-5" />
            </button>
            <SidebarContent onNavigate={() => setMobileOpen(false)} />
          </div>
        </div>
      )}

      {/* Sidebar desktop: card flutuante, fixo enquanto o conteúdo rola */}
      <aside className="hidden lg:block lg:w-[264px] lg:flex-shrink-0">
        <div className="sticky top-4 h-[calc(100vh-2rem)] overflow-hidden rounded-2xl border border-gray-200/70 bg-subtle">
          <SidebarContent />
        </div>
      </aside>
    </>
  );
};
